// お気に入り/並び順フィルタ制御
import { ref } from 'vue';
import { isFolderItem } from './useItemTypeGuards';

export const useFavoriteFilters = ({
  items,
  selectedItemRef,
  selectItem,
  currentFolderItemCount,
  setItemsTotal,
  selectedFolderId,
  searchScope,
}) => {
  const favoriteOnly = ref(false);
  const favoriteFilterUrl = ref('');
  const seqFilterUrl = ref('');
  const seqOrder = ref('desc');
  const seqProcessing = ref(false);
  const sortKey = ref('id');
  const sortFilterUrl = ref('');
  const favoriteToggleUrlTemplate = ref('');
  const favoriteProcessingIds = ref(new Set());

  // folder_id が送信可能な値か判定する（未選択/お気に入り仮想フォルダ等は非対象）
  const isValidFolderId = (folderId) => {
    if (folderId === null || folderId === undefined || folderId === '') {
      return false;
    }
    const parsed = Number(folderId);
    return Number.isFinite(parsed) && parsed > 0;
  };

  const initFavoritePreference = (dataSource) => {
    if (!dataSource) {
      favoriteOnly.value = false;
      favoriteFilterUrl.value = '';
      seqFilterUrl.value = '';
      seqOrder.value = 'desc';
      sortKey.value = 'id';
      sortFilterUrl.value = '';
      favoriteToggleUrlTemplate.value = '';
      return;
    }

    favoriteOnly.value = dataSource.getAttribute('data-favorite-only') === '1';
    favoriteFilterUrl.value =
      dataSource.getAttribute('data-favorite-filter-url') || '';
    seqFilterUrl.value = dataSource.getAttribute('data-seq-filter-url') || '';
    seqOrder.value =
      (dataSource.getAttribute('data-current-seq') || '').toLowerCase() === 'asc'
        ? 'asc'
        : 'desc';
    favoriteToggleUrlTemplate.value =
      dataSource.getAttribute('data-favorite-toggle-url-template') || '';
    sortKey.value = dataSource.getAttribute('data-current-sort') || 'id';
    sortFilterUrl.value = dataSource.getAttribute('data-sort-filter-url') || '';
  };

  // VideoController::SEARCH_CONDITION_KEYS と同じ一覧 (tags[]・ai_tags[]・cad_tags[] は `tags[`・`ai_tags[`・`cad_tags[` で始まるキーも対象)
  const searchConditionKeys = [
    'q',
    'search_scope',
    'tags_present',
    'tags',
    'ai_tags_present',
    'ai_tags',
    'cad_tags_present',
    'cad_tags',
    'kind',
    'date_from',
    'date_to',
    'favorite',
    'top',
  ];
  const isSearchConditionKey = (key) =>
    searchConditionKeys.includes(key) ||
    key.startsWith('tags[') ||
    key.startsWith('ai_tags[') ||
    key.startsWith('cad_tags[');

  const removeDashboardParamsFromAddressBar = () => {
    try {
      const url = new URL(window.location.href);
      const removableKeys = ['view', 'favorite', 'seq', 'sort'];
      const searchKeys = Array.from(new Set(url.searchParams.keys())).filter(
        isSearchConditionKey
      );
      const hasRemovable = removableKeys.some((key) => url.searchParams.has(key));
      if (!hasRemovable && searchKeys.length === 0) {
        return;
      }
      removableKeys.forEach((key) => {
        url.searchParams.delete(key);
      });
      // 検索条件はセッションに残っているため、アドレスバーからは消して page=1 にする。
      // 以後の F5・戻るは「page 付き・検索条件のキー無し」になり、サーバーが検索結果の id を使い回せる
      if (searchKeys.length > 0) {
        searchKeys.forEach((key) => {
          url.searchParams.delete(key);
        });
        if (!url.searchParams.has('page')) {
          url.searchParams.set('page', '1');
        }
      }
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      // noop: URL API not available
    }
  };

  const isFavoriteToggling = (videoId) =>
    favoriteProcessingIds.value.has(String(videoId ?? ''));

  const setFavoriteProcessing = (videoId, status) => {
    const normalizedId = String(videoId ?? '');
    if (!normalizedId) {
      return;
    }

    const next = new Set(favoriteProcessingIds.value);
    if (status) {
      next.add(normalizedId);
    } else {
      next.delete(normalizedId);
    }
    favoriteProcessingIds.value = next;
  };

  const resolveFavoriteToggleUrl = (videoId) => {
    const normalizedId = String(videoId ?? '');
    if (!normalizedId) {
      return null;
    }

    const template = favoriteToggleUrlTemplate.value;
    if (template && template.includes('__VIDEO_ID__')) {
      return template.replace('__VIDEO_ID__', normalizedId);
    }

    return `/dashboard/videos/${normalizedId}/favorite`;
  };

  const removeItemFromVisibleList = (videoId) => {
    const normalizedId = String(videoId ?? '');
    if (!normalizedId) {
      return;
    }

    // フォルダ行はお気に入り解除対象にならないため、動画行のみIDで除外判定する
    items.value = (items.value || []).filter(
      (entry) => isFolderItem(entry) || String(entry.id ?? '') !== normalizedId
    );
    setItemsTotal(Math.max(0, Number(currentFolderItemCount.value) - 1));
    if (
      selectedItemRef.value &&
      String(selectedItemRef.value.id ?? '') === normalizedId
    ) {
      selectItem(null);
    }
  };

  const toggleFavorite = async (item) => {
    const targetId = String(item?.id ?? '');
    if (!targetId || isFavoriteToggling(targetId)) {
      return;
    }

    const endpoint = resolveFavoriteToggleUrl(targetId);
    if (!endpoint) {
      return;
    }

    setFavoriteProcessing(targetId, true);
    try {
      const response = await window.axios.post(endpoint);
      const isFavorite = Boolean(response?.data?.is_favorite);
      item.is_favorite = isFavorite;

      const activeItem = selectedItemRef.value;
      if (activeItem && String(activeItem.id ?? '') === targetId) {
        activeItem.is_favorite = isFavorite;
      }

      if (favoriteOnly.value && !isFavorite) {
        removeItemFromVisibleList(targetId);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || 'お気に入りの更新に失敗しました。';
      window.alert(message);
    } finally {
      setFavoriteProcessing(targetId, false);
    }
  };

  const toggleFavoriteOnly = () => {
    const endpoint = favoriteFilterUrl.value || '/dashboard/videos/favorite/filter';
    const nextFavoriteOnly = !favoriteOnly.value;

    window.axios
      .post(endpoint, { favorite: nextFavoriteOnly ? 1 : 0 })
      .then(() => {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('view');
          url.searchParams.delete('favorite');
          url.searchParams.delete('page');
          url.searchParams.delete('folder_id');
          // 全体検索中/お気に入りへ遷移する場合はfolder_idを送らない
          // (送るとsearch_scopeが'folder'に強制上書きされてしまうため)
          if (
            !nextFavoriteOnly &&
            searchScope?.value !== 'all' &&
            isValidFolderId(selectedFolderId?.value)
          ) {
            url.searchParams.set('folder_id', String(selectedFolderId.value));
          }
          window.location.href = url.toString();
        } catch (error) {
          window.location.href = window.location.pathname;
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || 'お気に入りフィルタの更新に失敗しました。';
        window.alert(message);
      });
  };

  const setFavoriteOnly = async (nextFavoriteOnly) => {
    const target = Boolean(nextFavoriteOnly);
    if (favoriteOnly.value === target) {
      return true;
    }

    const endpoint = favoriteFilterUrl.value || '/dashboard/videos/favorite/filter';

    try {
      await window.axios.post(endpoint, { favorite: target ? 1 : 0 });
      favoriteOnly.value = target;
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message || 'お気に入りフィルタの更新に失敗しました。';
      window.alert(message);
      return false;
    }
  };

  const toggleSeqOrder = () => {
    if (seqProcessing.value) {
      return;
    }

    const nextSeq = seqOrder.value === 'asc' ? 'desc' : 'asc';
    const endpoint = seqFilterUrl.value || '/dashboard/videos/seq/filter';
    seqProcessing.value = true;

    window.axios
      .post(endpoint, { seq: nextSeq })
      .then((response) => {
        const appliedSeq =
          (response?.data?.seq || nextSeq || '').toLowerCase() === 'asc'
            ? 'asc'
            : 'desc';
        seqOrder.value = appliedSeq;
        try {
          const url = new URL(window.location.href);
          ['view', 'favorite', 'seq', 'page'].forEach((key) => {
            url.searchParams.delete(key);
          });
          url.searchParams.delete('folder_id');
          // 全体検索中/お気に入り表示中はfolder_idを送らない
          // (送るとsearch_scopeが'folder'に強制上書きされてしまうため)
          if (
            !favoriteOnly.value &&
            searchScope?.value !== 'all' &&
            isValidFolderId(selectedFolderId?.value)
          ) {
            url.searchParams.set('folder_id', String(selectedFolderId.value));
          }
          url.searchParams.set('seq', appliedSeq);
          window.location.href = url.toString();
        } catch (error) {
          window.location.href = window.location.pathname + '?seq=' + appliedSeq;
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || '並び順の更新に失敗しました。';
        window.alert(message);
      })
      .finally(() => {
        seqProcessing.value = false;
      });
  };

  const setSortOrder = (column) => {
    const allowedColumns = ['id', 'name', 'upload_filesize', 'created_at'];
    if (!allowedColumns.includes(column)) {
      return;
    }

    const isSameColumn = sortKey.value === column;
    let nextSeq;
    if (isSameColumn) {
      nextSeq = seqOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      nextSeq = column === 'id' ? 'desc' : 'asc';
    }

    const endpoint = sortFilterUrl.value || '/dashboard/videos/sort/filter';

    window.axios
      .post(endpoint, { sort: column, seq: nextSeq })
      .then(() => {
        sortKey.value = column;
        seqOrder.value = nextSeq;
        try {
          const url = new URL(window.location.href);
          ['view', 'favorite', 'seq', 'sort', 'page'].forEach((key) => {
            url.searchParams.delete(key);
          });
          url.searchParams.delete('folder_id');
          // 全体検索中/お気に入り表示中はfolder_idを送らない
          // (送るとsearch_scopeが'folder'に強制上書きされてしまうため)
          if (
            !favoriteOnly.value &&
            searchScope?.value !== 'all' &&
            isValidFolderId(selectedFolderId?.value)
          ) {
            url.searchParams.set('folder_id', String(selectedFolderId.value));
          }
          url.searchParams.set('sort', column);
          url.searchParams.set('seq', nextSeq);
          window.location.href = url.toString();
        } catch (error) {
          window.location.href =
            window.location.pathname + '?sort=' + column + '&seq=' + nextSeq;
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || 'ソート順の更新に失敗しました。';
        window.alert(message);
      });
  };

  return {
    favoriteOnly,
    initFavoritePreference,
    removeDashboardParamsFromAddressBar,
    toggleSeqOrder,
    toggleFavoriteOnly,
    setFavoriteOnly,
    toggleFavorite,
    isFavoriteToggling,
    seqOrder,
    sortKey,
    setSortOrder,
  };
};
